/** Illustrative plan marketing copy for the public About page.
 * Quotas match API plan_templates seeds; dollar prices are working anchors only.
 * Voice: plain language for non-technical organizations and teams.
 */

export const PLAN_PRICE_DISCLAIMER =
  'Prices are working anchors and may change. Self-serve billing is not live yet — plans are assigned by Circa today.';

export const PLANS = [
  {
    id: 'free',
    name: 'Free',
    priceLabel: '$0',
    priceNote: 'forever',
  },
  {
    id: 'pro',
    name: 'Pro',
    priceLabel: '~$49',
    priceNote: '/ workplace / mo',
  },
  {
    id: 'business',
    name: 'Business',
    priceLabel: '~$149',
    priceNote: '/ workplace / mo',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceLabel: 'Custom',
    priceNote: 'contact us',
  },
];

/** Display label for an org plan id (falls back to a tidy string). */
export function planDisplayName(planId) {
  const found = PLANS.find((p) => p.id === planId);
  if (found) return found.name;
  const raw = String(planId || '').trim();
  if (!raw) return 'Free';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Matrix rows: cell values are display strings; comingSoon marks unenforced packaging. */
export const PLAN_MATRIX_ROWS = [
  {
    label: 'Members',
    cells: {
      free: 'Up to 10',
      pro: '15 included',
      business: '50 included',
      enterprise: 'Custom',
    },
  },
  {
    label: 'Extra members',
    cells: {
      free: '—',
      pro: '~$8 / member / mo',
      business: '~$12 / member / mo',
      enterprise: 'Custom',
    },
  },
  {
    label: 'Storage',
    cells: {
      free: '5 GB',
      pro: '100 GB',
      business: '1 TB',
      enterprise: 'Custom',
    },
  },
  {
    label: 'Monthly catch-up summaries',
    cells: {
      free: '20',
      pro: '200',
      business: '1,000',
      enterprise: 'Custom',
    },
  },
  {
    label: 'Team boards (optional)',
    cells: {
      free: '1',
      pro: 'Unlimited',
      business: 'Unlimited',
      enterprise: 'Unlimited',
    },
  },
  {
    label: 'Audit log & export',
    cells: {
      free: '—',
      pro: '—',
      business: 'Yes (365-day retention)',
      enterprise: 'Yes (365-day retention)',
    },
  },
  {
    label: 'Sign in with your organization’s accounts',
    cells: {
      free: '—',
      pro: '—',
      business: 'Yes',
      enterprise: 'Yes',
    },
  },
  {
    label: 'Email digests',
    comingSoon: true,
    cells: {
      free: '—',
      pro: '—',
      business: 'Coming soon',
      enterprise: 'Coming soon',
    },
  },
  {
    label: 'Sharing beyond the workplace',
    cells: {
      free: '—',
      pro: '—',
      business: 'Yes',
      enterprise: 'Yes',
    },
  },
  {
    label: 'Connections for other tools',
    comingSoon: true,
    cells: {
      free: '—',
      pro: '—',
      business: 'Coming soon',
      enterprise: 'Coming soon',
    },
  },
];

export const FEATURES_NOW = [
  'Private workplaces, teams, and updates',
  'Channels and direct messages',
  'Partner conversations and handoffs between workplaces',
  'Catch-up summaries when you’ve been away',
  'Needs attention for critical posts, mentions, and handoffs',
  'Summarize a thread or card from its page',
  'Search across your workplace and messages',
  'Notifications in the app',
  'Files on posts and in shared libraries',
  'In-app preview for Office docs and spreadsheets',
  'Optional boards on teams',
  'Sign in with your organization’s accounts (Business and Enterprise)',
  'Circa Social profile and personal home feed',
  'Public communities, org Social profiles, and explore',
  'Workplace Circa Social when your org enables presence',
  'Optional publish from your workplace to its community when enabled',
  'History, plan usage, and cleanup tools for admins',
  'Audit log view and CSV export on Business and above',
];

export const FEATURES_COMING = [
  'Email / weekly digests',
  'Connections for other tools',
  'Self-serve upgrades and billing',
];

/** Workplace-focused everyday stories */
export const USE_CASES = [
  {
    title: 'Catch up after time away',
    body: 'Come back to a short briefing and a Needs attention list—clear pointers to what changed and what still needs you.',
  },
  {
    title: 'Stay aligned across groups',
    body: 'Keep a shared picture of what’s moving without jumping into every conversation.',
  },
  {
    title: 'Get work done together',
    body: 'Updates, files, and optional boards where your team already works.',
  },
  {
    title: 'Keep the workplace private',
    body: 'Invite-only membership, history when you need it, and room to grow when the group is ready.',
  },
];

/** Communities & Social — human framing of the two-surface product */
export const COMMUNITY_POINTS = [
  {
    title: 'Workplace',
    body: 'A private, invite-only home for your organization—teams, updates, and conversations that stay inside.',
  },
  {
    title: 'Social',
    body: 'Your personal Circa Social profile and feed beyond a single workplace—stay close to people, not only one org.',
  },
  {
    title: 'Communities',
    body: 'Public spaces around shared interests or an organization’s Social face. Followers join the community—not the private workplace.',
  },
  {
    title: 'The idea',
    body: 'Share thoughtful updates outward—optionally from your workplace to its community when that space is enabled—listen to the circle around you, and bring what you learn back into the work inside.',
  },
];

export const SOCIAL_STATUS_NOTE =
  'Circa Social is available today—profiles, communities, and explore. Workplaces stay private by default. When your workplace’s community is enabled, you can choose to publish updates there.';
